/**
 * O editor de um exercício do catálogo.
 *
 * Três idiomas lado a lado, e não um formulário por idioma: a decisão de
 * publicar depende de comparar o que existe. Ver que o inglês está bom e que o
 * português não existe é a informação que faz a pessoa escrever o português.
 *
 * **A procedência é só de leitura.** Fonte, licença e autor aparecem, e não há
 * campo para editá-los — um gatilho no banco recusaria a gravação de qualquer
 * forma (migration `0008`). São a atribuição legal do conteúdo: mudá-los por
 * engano significaria exibir material creditando a pessoa errada.
 *
 * Há **um** caminho para a procedência mudar, e ele não é um campo: enviar
 * mídia própria assume o exercício inteiro (migration `0009`). Abrir mão dela
 * por completo é permitido; reescrevê-la para outro valor, não. Por isso o
 * aviso aparece antes de escolher o arquivo — quem só quer trocar o vídeo
 * precisa saber que o texto importado vai junto.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import { LOCALES, type CurationItem, type Locale } from '@/features/curation/api';
import { titleOf } from '@/features/curation/title';
import { CATEGORIES, EQUIPMENT, LEVELS, MODALITIES } from '@/features/curation/vocabulary';
import {
  curationErrorKey,
  useClearMedia,
  useCurationItem,
  useMediaLimits,
  useSaveClassification,
  useSaveTranslation,
  useSetPublication,
  useUploadMedia,
} from '@/features/curation/useCuration';
import { StatusBadge } from './CurationPage';

export default function CurationDetailPage() {
  const { t } = useTranslation(['curation', 'common']);
  const { exerciseId = '' } = useParams();
  const item = useCurationItem(exerciseId);

  if (item.isPending) {
    return (
      <p className="px-5 py-6 text-(--color-sand-700)" aria-busy="true">
        {t('common:state.loading')}
      </p>
    );
  }

  if (item.isError || !item.data) {
    return (
      <div className="px-5 py-6">
        <Alert tone="error" live="assertive">
          {t(curationErrorKey(item.error))}
        </Alert>
        <Link to="/curation" className="mh-button mh-button--ghost mt-4">
          {t('common:action.back')}
        </Link>
      </div>
    );
  }

  return <Editor item={item.data} />;
}

function Editor({ item }: { item: CurationItem }) {
  const { t } = useTranslation(['curation', 'common']);
  const publicar = useSetPublication();

  // O nome do exercício, e não a palavra "Exercício". Numa fila de centenas,
  // um título genérico deixa a pessoa sem saber o que está editando — e a
  // curadoria é feita alternando entre a lista e o detalhe.
  const titulo = titleOf(item);

  return (
    <div className="px-5 py-6">
      <Link to="/curation" className="mh-button mh-button--ghost -ml-5">
        {t('common:action.back')}
      </Link>

      <div className="mt-3 flex items-start justify-between gap-3">
        <h1 className="text-2xl">{titulo.text || t('curation:detail.untitled')}</h1>
        <StatusBadge status={item.publication_status} />
      </div>

      {/* Em que idioma está o que se está lendo. Sem a marca, um título em
          espanhol passa por português mal escrito — e a decisão de publicar
          depende justamente de saber que o português ainda não existe. */}
      {!titulo.translated && titulo.locale ? (
        <p className="mt-1 text-sm text-(--color-sand-600)">
          {t('curation:inLanguage', { locale: t(`curation:locale.${titulo.locale}`) })}
        </p>
      ) : null}

      <Media item={item} />
      <Provenance item={item} />

      <h2 className="mt-8 text-lg font-semibold">{t('curation:detail.languages')}</h2>
      <Translations item={item} />

      <h2 className="mt-8 text-lg font-semibold">{t('curation:detail.classification')}</h2>
      <Classification item={item} />

      <h2 className="mt-8 text-lg font-semibold">{t('curation:detail.publication')}</h2>

      {item.blockers.length > 0 ? (
        <div className="mt-3">
          <Alert tone="warning">
            <p className="font-semibold">{t('curation:detail.blockedTitle')}</p>
            <ul className="mt-1 list-disc pl-4">
              {item.blockers.map((motivo) => (
                <li key={motivo}>{t(`curation:blocker.${motivo}`)}</li>
              ))}
            </ul>
          </Alert>
        </div>
      ) : null}

      {publicar.isError ? (
        <div className="mt-3">
          <Alert tone="error" live="assertive">
            {t(curationErrorKey(publicar.error))}
          </Alert>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          // O botão fica desabilitado quando falta algo — e o aviso acima diz o
          // quê. Deixá-lo clicável para "descobrir" o motivo transformaria a
          // curadoria em tentativa e erro.
          disabled={
            item.blockers.length > 0 ||
            item.publication_status === 'published' ||
            publicar.isPending
          }
          onClick={() => publicar.mutate({ exerciseId: item.exercise_id, status: 'published' })}
          className="mh-button mh-button--primary"
        >
          {t('curation:action.publish')}
        </button>

        <button
          type="button"
          disabled={item.publication_status === 'draft' || publicar.isPending}
          onClick={() => publicar.mutate({ exerciseId: item.exercise_id, status: 'draft' })}
          className="mh-button mh-button--ghost"
        >
          {t('curation:action.unpublish')}
        </button>

        <button
          type="button"
          disabled={item.publication_status === 'archived' || publicar.isPending}
          onClick={() => publicar.mutate({ exerciseId: item.exercise_id, status: 'archived' })}
          className="mh-button mh-button--ghost"
        >
          {t('curation:action.archive')}
        </button>
      </div>
    </div>
  );
}

function Media({ item }: { item: CurationItem }) {
  const { t } = useTranslation(['curation', 'common']);
  const limites = useMediaLimits();
  const enviar = useUploadMedia();
  const limpar = useClearMedia();

  const [gerado, setGerado] = useState(true);
  const arquivoRef = useRef<HTMLInputElement>(null);

  const importado = item.source !== 'movhuman';
  const ocupado = enviar.isPending || limpar.isPending;

  function escolher(event: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = event.target.files?.[0];
    // Limpa o campo para que escolher **o mesmo arquivo** de novo, depois de um
    // erro, dispare o evento outra vez — sem isto a segunda tentativa não faz
    // nada e parece que o botão quebrou.
    event.target.value = '';
    if (arquivo)
      enviar.mutate({ exerciseId: item.exercise_id, file: arquivo, isGenerated: gerado });
  }

  return (
    <section className="mt-4">
      {item.media_url && item.media_kind ? (
        // `media_kind` existe justamente para esta escolha: adivinhar pela
        // extensão quebra na primeira URL sem extensão ou assinada (`0007`).
        <div className="overflow-hidden rounded-2xl border border-(--color-sand-200)">
          {item.media_kind === 'video' ? (
            <video src={item.media_url} controls playsInline className="w-full" />
          ) : (
            <img src={item.media_url} alt="" className="w-full" />
          )}
        </div>
      ) : (
        <p className="text-sm text-(--color-sand-600)">{t('curation:media.none')}</p>
      )}

      {item.media_is_generated ? (
        <p className="mt-2 text-xs text-(--color-sand-600)">{t('curation:media.generatedMark')}</p>
      ) : null}

      {/* O aviso aparece **antes** de escolher o arquivo, e não depois de o
          envio falhar: quem só quer trocar o vídeo precisa saber que o texto
          importado vai junto. */}
      {importado ? (
        <div className="mt-3">
          <Alert tone="warning">
            <p className="font-semibold">{t('curation:media.takeoverTitle')}</p>
            <p className="mt-1">{t('curation:media.takeoverBody')}</p>
          </Alert>
        </div>
      ) : null}

      {enviar.isError ? (
        <div className="mt-3">
          <Alert tone="error" live="assertive">
            {t(curationErrorKey(enviar.error))}
          </Alert>
        </div>
      ) : null}

      <label className="mt-3 flex items-center gap-2 text-sm text-(--color-sand-800)">
        <input
          type="checkbox"
          checked={gerado}
          onChange={() => setGerado(!gerado)}
          className="size-5 rounded border-(--color-sand-300)"
        />
        {t('curation:media.isGenerated')}
      </label>

      <input
        ref={arquivoRef}
        type="file"
        accept={limites.data?.accepted.join(',')}
        onChange={escolher}
        className="hidden"
      />

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={ocupado}
          onClick={() => arquivoRef.current?.click()}
          className="mh-button mh-button--secondary"
        >
          {enviar.isPending
            ? t('common:state.loading')
            : item.media_url
              ? t('curation:media.replace')
              : t('curation:media.send')}
        </button>

        {item.media_url ? (
          <button
            type="button"
            disabled={ocupado}
            onClick={() => limpar.mutate({ exerciseId: item.exercise_id })}
            className="mh-button mh-button--ghost"
          >
            {t('curation:media.remove')}
          </button>
        ) : null}
      </div>

      {limites.data ? (
        <p className="mt-2 text-xs text-(--color-sand-600)">
          {t('curation:media.limits', {
            megabytes: Math.round(limites.data.max_bytes / (1024 * 1024)),
          })}
        </p>
      ) : null}
    </section>
  );
}

function Provenance({ item }: { item: CurationItem }) {
  const { t } = useTranslation(['curation']);

  return (
    <dl className="mt-4 rounded-2xl bg-(--color-sand-50) p-4 text-sm">
      <div className="flex justify-between gap-3">
        <dt className="text-(--color-sand-600)">{t('curation:provenance.source')}</dt>
        <dd className="text-(--color-sand-900)">{item.source}</dd>
      </div>
      {item.license_name ? (
        <div className="mt-2 flex justify-between gap-3">
          <dt className="text-(--color-sand-600)">{t('curation:provenance.license')}</dt>
          <dd className="text-right text-(--color-sand-900)">
            {item.license_url ? (
              <a href={item.license_url} target="_blank" rel="noreferrer" className="underline">
                {item.license_name}
              </a>
            ) : (
              item.license_name
            )}
          </dd>
        </div>
      ) : null}
      {item.license_author ? (
        <div className="mt-2 flex justify-between gap-3">
          <dt className="text-(--color-sand-600)">{t('curation:provenance.author')}</dt>
          <dd className="text-right text-(--color-sand-900)">{item.license_author}</dd>
        </div>
      ) : null}
      <p className="mt-3 text-xs text-(--color-sand-600)">{t('curation:provenance.locked')}</p>
    </dl>
  );
}

function Translations({ item }: { item: CurationItem }) {
  const { t } = useTranslation(['curation', 'common']);
  const [locale, setLocale] = useState<Locale>('pt-BR');
  const salvar = useSaveTranslation();

  const existente = item.translations.find((linha) => linha.locale === locale);

  const [title, setTitle] = useState(existente?.title ?? '');
  const [instructions, setInstructions] = useState(existente?.instructions ?? '');
  const [reviewed, setReviewed] = useState(existente?.reviewed ?? true);

  // Trocar de aba recarrega os campos daquele idioma. Sem isto, o texto do
  // português continuaria na tela sob o rótulo do inglês — e a pessoa salvaria
  // o português por cima do inglês sem perceber.
  useEffect(() => {
    const linha = item.translations.find((atual) => atual.locale === locale);
    setTitle(linha?.title ?? '');
    setInstructions(linha?.instructions ?? '');
    setReviewed(linha?.reviewed ?? true);
  }, [locale, item.translations]);

  const sujo =
    title !== (existente?.title ?? '') ||
    instructions !== (existente?.instructions ?? '') ||
    reviewed !== (existente?.reviewed ?? true);

  return (
    <div className="mt-3">
      <div role="tablist" className="flex gap-2">
        {LOCALES.map((opcao) => {
          const tem = item.translations.some((linha) => linha.locale === opcao);
          return (
            <button
              key={opcao}
              type="button"
              role="tab"
              aria-selected={locale === opcao}
              onClick={() => setLocale(opcao)}
              className={[
                'flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
                locale === opcao
                  ? 'border-(--color-amber-500) bg-(--color-amber-50)'
                  : 'border-(--color-sand-200) bg-(--color-sand-0)',
              ].join(' ')}
            >
              <span className="block">{t(`curation:locale.${opcao}`)}</span>
              {/* Qual idioma falta é a informação que organiza o trabalho. */}
              <span className="mt-0.5 block text-xs text-(--color-sand-600)">
                {tem ? t('curation:language.present') : t('curation:language.absent')}
              </span>
            </button>
          );
        })}
      </div>

      {existente ? (
        <p className="mt-3 text-xs text-(--color-sand-600)">
          {t(`curation:origin.${existente.origin}`)}
        </p>
      ) : null}

      <label className="mt-3 block">
        <span className="text-sm font-medium text-(--color-sand-800)">
          {t('curation:field.title')}
        </span>
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={160}
          className="mt-1 w-full rounded-xl border border-(--color-sand-200) px-3 py-3"
        />
      </label>

      <label className="mt-3 block">
        <span className="text-sm font-medium text-(--color-sand-800)">
          {t('curation:field.instructions')}{' '}
          <span className="font-normal text-(--color-sand-600)">
            {t('curation:field.optional')}
          </span>
        </span>
        <textarea
          value={instructions}
          onChange={(event) => setInstructions(event.target.value)}
          maxLength={4000}
          rows={6}
          className="mt-1 w-full rounded-xl border border-(--color-sand-200) px-3 py-3"
        />
      </label>

      <label className="mt-3 flex items-center gap-2 text-sm text-(--color-sand-800)">
        <input
          type="checkbox"
          checked={reviewed}
          onChange={() => setReviewed(!reviewed)}
          className="size-5 rounded border-(--color-sand-300)"
        />
        {t('curation:field.reviewed')}
      </label>

      {salvar.isError ? (
        <div className="mt-3">
          <Alert tone="error" live="assertive">
            {t(curationErrorKey(salvar.error))}
          </Alert>
        </div>
      ) : null}

      <button
        type="button"
        disabled={!sujo || title.trim() === '' || salvar.isPending}
        onClick={() =>
          salvar.mutate({
            exerciseId: item.exercise_id,
            locale,
            draft: {
              title: title.trim(),
              instructions: instructions.trim() === '' ? null : instructions.trim(),
              reviewed,
            },
          })
        }
        className="mh-button mh-button--primary mt-4 w-full"
      >
        {t('curation:action.saveLanguage')}
      </button>
    </div>
  );
}

function Classification({ item }: { item: CurationItem }) {
  const { t } = useTranslation(['curation', 'common']);
  const salvar = useSaveClassification();

  const [modality, setModality] = useState(item.modality);
  const [category, setCategory] = useState(item.category);
  const [level, setLevel] = useState(item.level);
  const [equipment, setEquipment] = useState<string[]>(item.equipment);

  function alternar(valor: string) {
    setEquipment(
      equipment.includes(valor)
        ? equipment.filter((atual) => atual !== valor)
        : [...equipment, valor],
    );
  }

  return (
    <div className="mt-3">
      <Select
        label={t('curation:field.modality')}
        value={modality}
        onChange={setModality}
        options={MODALITIES}
        prefix="curation:modality"
      />
      <Select
        label={t('curation:field.category')}
        value={category}
        onChange={setCategory}
        options={CATEGORIES}
        prefix="curation:category"
      />
      <Select
        label={t('curation:field.level')}
        value={level}
        onChange={setLevel}
        options={LEVELS}
        prefix="curation:level"
      />

      <fieldset className="mt-3">
        <legend className="text-sm font-medium text-(--color-sand-800)">
          {t('curation:field.equipment')}
        </legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {EQUIPMENT.map((opcao) => (
            <button
              key={opcao}
              type="button"
              aria-pressed={equipment.includes(opcao)}
              onClick={() => alternar(opcao)}
              className={[
                'rounded-full border px-3 py-2 text-sm transition-colors',
                equipment.includes(opcao)
                  ? 'border-(--color-amber-500) bg-(--color-amber-50)'
                  : 'border-(--color-sand-200) bg-(--color-sand-0)',
              ].join(' ')}
            >
              {t(`curation:equipment.${opcao}`)}
            </button>
          ))}
        </div>
      </fieldset>

      {salvar.isError ? (
        <div className="mt-3">
          <Alert tone="error" live="assertive">
            {t(curationErrorKey(salvar.error))}
          </Alert>
        </div>
      ) : null}

      <button
        type="button"
        disabled={salvar.isPending}
        onClick={() =>
          salvar.mutate({
            exerciseId: item.exercise_id,
            draft: { modality, category, level, equipment },
          })
        }
        className="mh-button mh-button--secondary mt-4 w-full"
      >
        {t('curation:action.saveClassification')}
      </button>
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  prefix,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<string>;
  prefix: string;
}) {
  const { t } = useTranslation(['curation']);

  return (
    <label className="mt-3 block">
      <span className="text-sm font-medium text-(--color-sand-800)">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded-xl border border-(--color-sand-200) bg-(--color-sand-0) px-3 py-3"
      >
        {options.map((opcao) => (
          <option key={opcao} value={opcao}>
            {t(`${prefix}.${opcao}`)}
          </option>
        ))}
      </select>
    </label>
  );
}
